import type { PodBrand } from '@/types/pod'

/**
 * The JNC Safety Services pod — branding layer (browser side).
 *
 * A SALES DEMO, NOT A CUSTOMER. JNC Safety Services Limited (Woodbine Farm
 * Business Centre, Truro Business Park, Truro TR3 6BW, company no. 04524302)
 * has not bought anything and has not signed a DPA. Consequences, all
 * deliberate and identical in kind to swsteel, ctc and fts:
 *   - registry entry carries NO `billing` flag and NO `customerSince`
 *     (`billing: 'operator'` marks the operator's OWN products; absence of
 *     `customerSince` is what marks a demo)
 *   - `notifyPhone` is the OPERATOR'S mobile — an enquiry captured here is a
 *     demo artefact, not their lead, and must not reach them
 *   - no `publishDomain`, no `missedCall`: both point at a domain and a phone
 *     line the operator does not control
 *
 * SOURCE OF TRUTH. Every value below is from jncsafetyservices.co.uk, read
 * 18 Sep 2026 — the phone, email and address from the site header, the contact
 * page and the footer; the palette from a screenshot of /training-courses/
 * taken the same day.
 *
 * NO OPENING HOURS ANYWHERE, same as fts. The site publishes none. What it
 * DOES publish is worse than nothing for an assistant: the services page
 * promises availability 24 hours a day, 365 days a year, TO CLIENTS. A website
 * visitor is not a client, so that claim must never reach one, and no hours are
 * invented in its place. If they buy, ask them.
 *
 * WELCOME INTRO SPLITS TRAINING FROM CONSULTANCY, DELIBERATELY. Two quite
 * different customers arrive at this site: someone who wants to ATTEND a course
 * — a labourer chasing a CSCS card, a supervisor needing SSSTS — and an
 * employer who wants JNC to COME AND DO SOMETHING, a risk assessment, a fire
 * risk assessment, CDM advice, an audit. Opening with only one of them loses
 * the other, and working out which is typing is the first useful act of the
 * conversation.
 *
 * STARTERS INCLUDE THE COURSE-VERSUS-SERVICE TRAP ON PURPOSE. "Do you do risk
 * assessments for us?" is the question this pod most needs to get right. On
 * their website, risk assessment exists BOTH as a course somebody attends
 * (/course/risk-assessment/) AND as a service JNC carry out
 * (/course/risk-assessments/) — near-identical slugs under the same path, with
 * no signal distinguishing them. Answering a consultancy enquiry with a
 * classroom place, or the reverse, is wrong in a way the enquirer cannot
 * detect until somebody quotes them. Putting it on the front of the widget
 * makes the correct behaviour the first thing their team sees.
 *
 * "HOW DO I GET A CSCS CARD" IS THE OTHER DELIBERATE STARTER. Their HSA page
 * holds the answer — the one-day course plus the CITB touchscreen test gets the
 * Green Card — and it sits three clicks from the homepage. It is the question
 * their front desk answers most and their website answers worst, which makes it
 * the best single demonstration of what this thing is for.
 *
 * PALETTE — HAZARD YELLOW AND BLACK, READ FROM THE RENDERED SITE.
 * Their brand is safety signage: a bright hazard yellow, near-black text, a
 * white page, and black-and-yellow chevron tape running down both edges. The
 * navigation bar and the course-category accordion bars are solid yellow with
 * black condensed type on them. The logo is a yellow hard hat with JNC in black.
 *
 * THE YELLOW IS MAPPED AS ACCENT, NOT PRIMARY, AND THAT IS DELIBERATE. In their
 * design the yellow is a SURFACE that black text sits on — it is never an action
 * colour. Assigning it to `--color-brand-primary` would produce a widget of
 * yellow buttons with white labels: a contrast failure (yellow on white is
 * roughly 1.3:1, nowhere near the 4.5:1 floor) and visually nothing like their
 * site. So primary is their near-black, which is what actually anchors every
 * page, and the yellow carries the header band, the starter chips and the
 * borders, always with black on top of it. That reads as their brand instead of
 * a caricature of it.
 *
 * WHAT IS NOT COPIED: the chevron hazard-tape edging, the condensed display
 * face and the 2014-era photography. The tape is page furniture on a wide
 * desktop layout and becomes noise in a chat panel; the rest is simply dated,
 * and a demo that imitates the dated parts of a client's site reads as a
 * pastiche rather than a product. Their colours, not their decade.
 *
 * builtByForedesk IS ON, as on swsteel, ctc and fts: a pitch asset shown to a
 * prospect, not a business's own front of house, so the credit is wanted.
 */
export const jncPod: PodBrand = {
  id: 'jnc',
  brandName: 'JNC Safety Services',
  productName: 'JNC Safety Services',
  assistantName: 'Morwenna',
  tagline: 'Health and safety training and consultancy in Cornwall',
  roleLabel: 'training and safety assistant',
  welcomeIntro:
    "Tell me what you need and I'll point you at the right thing. Training for yourself or your team — CSCS and CITB cards, first aid, fire, asbestos, NEBOSH, manual handling? Or do you want JNC to come and do something — a risk assessment, a fire risk assessment, CDM advice, an audit? Say which and roughly what it's for, and I'll take it from there and get it to the office.",
  starters: [
    'How do I get a CSCS card?',
    'Do I need SSSTS or SMSTS?',
    'First aid training for our staff',
    'Do you do risk assessments for us?',
  ],
  inputPrompt: 'Ask Morwenna about training or services',
  footerNote:
    'Morwenna answers from JNC Safety Services\u2019 own information \u2014 anything else goes straight to the office. Not health and safety, first aid, fire or legal advice, and not for anything to do with an accident or a claim; call 999 in an emergency.',
  builtByForedesk: true,
  theme: {
    // Sampled from a screenshot of jncsafetyservices.co.uk/training-courses/,
    // 18 Sep 2026. Tune once seen live beside their logo.
    '--color-brand-primary': '26 26 26', // near-black #1A1A1A — their type and hat outline
    '--color-brand-primary-hover': '0 0 0', // #000000
    '--color-brand-accent': '244 224 75', // hazard yellow #F4E04B — nav and accordion bars
    '--color-surface-base': '250 250 247', // faint warm off-white behind the white page
    '--color-surface-raised': '255 255 255',
    '--color-surface-subtle': '253 247 205', // #FDF7CD — the pale yellow mailing-list band
    '--color-text-primary': '26 26 26',
    '--color-text-muted': '90 90 90',
    '--color-text-danger': '178 47 47',
    '--color-border-subtle': '226 226 218',
  },
  contact: {
    phone: '01872 262261',
    email: 'safety@jncsafetyservices.co.uk',
  },
}
